/**
 * Directory Search fallback for call-history enrichment (BEMS01995768).
 *
 * When DSS `lookupByPhoneNumbers` misses or times out (common for org mobile
 * numbers), use the same DSS Search REST API as the dialpad (`lookupByPhoneOrExtension`).
 */

import {
  lookupByPhoneOrExtension,
  type DirectorySearchUser,
} from '../../ms-teams-sdk/service/DirectorySearchService';
import { isSuffixMatch } from './selector';
import type { EntitySelectionResult } from './types';

/** Collect all phone values from a directory search user record. */
export function collectDirectoryUserPhones(user: DirectorySearchUser): string[] {
  const phones: string[] = [];

  if (user.mobilePhone) {
    phones.push(user.mobilePhone);
  }
  if (user.businessPhones?.length) {
    phones.push(...user.businessPhones);
  }
  if (user.phones?.length) {
    phones.push(...user.phones);
  }
  if (user.phoneNumbersWithType?.length) {
    phones.push(...user.phoneNumbersWithType.map((p) => p.value));
  }

  return phones;
}

/** Pick the directory user whose profile phones match the call-history number. */
export function selectDirectoryUserForPhone(
  users: DirectorySearchUser[],
  phone: string
): DirectorySearchUser | undefined {
  if (!users.length) {
    return undefined;
  }

  const matched = users.find((user) =>
    collectDirectoryUserPhones(user).some((candidate) => isSuffixMatch(phone, candidate))
  );

  return matched ?? (users.length === 1 ? users[0] : undefined);
}

/** Resolve display name via Directory Search API (dialpad path). */
export async function lookupEntityByDirectorySearch(
  phone: string
): Promise<EntitySelectionResult | undefined> {
  const users = await lookupByPhoneOrExtension(phone, 10);
  const match = selectDirectoryUserForPhone(users, phone);

  if (!match?.displayName || !match.id) {
    return undefined;
  }

  return {
    displayName: match.displayName,
    entityId: match.id,
    provider: 'directory-search',
  };
}
